import cx from "classnames";
import Image from "next/image";
import Carousel, { Alignment } from "nuka-carousel";
import { useState } from "react";

import { useSettings } from "shared/SettingsProvider/useSettings";
import { useProductPageContext } from "shared/product/ProductPageContext/useProductPageContext";
import { PictureCard } from "shared/product/ProductSection/ProductImages/PictureCard";
import { StrapiImage, StrapiImageData } from "ui/StrapiImage";

type ProductImagesProps = {
  className?: string;
};

export const ProductImages = ({ className }: ProductImagesProps) => {
  const { isBlackFriday } = useSettings();
  const {
    currentVariant,
    family,
    subscriptionDropStickerUrl,
    hasVariantWithUpfrontPriceDrop,
  } = useProductPageContext();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const isBlackWeekProduct =
    currentVariant?.tagSlugs?.includes("black-week") ?? false;
  const isBlackFridayDiscount =
    isBlackFriday && isBlackWeekProduct && family.category === "available";

  const images = [
    currentVariant?.combinedPicture,
    currentVariant?.backPicture,
    currentVariant?.frontPicture,
  ].filter(Boolean) as StrapiImageData[];

  const handleClick = (index: number) => {
    setSelectedImageIndex(index);
  };

  return (
    <div className={cx("grow max-w-960", className)}>
      {/* Mobile version */}
      <div className="relative lg:hidden">
        <Carousel
          className="mt-5"
          cellAlign={Alignment.Center}
          defaultControlsConfig={{
            nextButtonClassName: "hidden",
            prevButtonClassName: "hidden",
            pagingDotsContainerClassName: "flex gap-3 product-images-carousel",
            pagingDotsStyle: {
              marginBottom: "6px",
            },
          }}
        >
          {images?.map((image, index) => (
            <div key={index} className="flex justify-center items-center mb-12">
              <StrapiImage
                size="medium"
                image={image}
                className="w-64 h-64"
                loader={true}
                priority
                sizes="50vw"
              />
            </div>
          ))}
        </Carousel>

        {isBlackFridayDiscount && (
          <div className="absolute top-10 left-1/2 w-[153px] h-[91px]">
            <Image src="/images/christmas/product-sticker.webp" layout="fill" />
          </div>
        )}

        {/* DAS mentions */}
        <div className="flex overflow-hidden gap-2 justify-center mt-3 w-full text-2xs text-gray-4 sm:gap-10 sm:text-xs 2xs:gap-5">
          {family?.dasValues?.map((dasValue, index) => (
            <p key={index} className="whitespace-nowrap">
              {dasValue}
            </p>
          ))}
        </div>
      </div>

      {/* Desktop version sticky */}
      <div className="hidden sticky -top-[60px] z-10 flex-col items-center mb-10 lg:flex">
        <div className="flex justify-center items-center mt-24 text-2xs text-gray-4 sm:text-xs lg:flex">
          <div className="flex flex-col gap-4 justify-center">
            {images?.map((image, index) => (
              <PictureCard
                key={index}
                image={image}
                onClick={() => handleClick(index)}
                className={cx("cursor-pointer", {
                  "bg-yellow/20": selectedImageIndex === index,
                })}
                loader={true}
              />
            ))}
          </div>

          <div className="relative w-[30rem] h-[30rem]">
            {/* Badge */}
            {subscriptionDropStickerUrl && hasVariantWithUpfrontPriceDrop && (
              <div
                className={cx(
                  "absolute -top-16 z-10 max-w-[200px]",
                  selectedImageIndex === 0 ? "right-2" : "right-10",
                )}
              >
                <StrapiImage
                  isFuture={true}
                  image={{
                    ...{
                      thumbnail: { url: subscriptionDropStickerUrl },
                    },
                    alternativeText: "price drop sticker",
                  }}
                  size="thumbnail"
                  sizes="300vw"
                  priority
                />
              </div>
            )}

            <StrapiImage
              size="medium"
              sizes="50vw"
              className="w-full h-full"
              image={images?.[selectedImageIndex]}
              priority={true}
              loader={true}
            />

            {/* DAS mentions */}
            <div className="flex overflow-hidden gap-2 justify-center mt-5 w-full text-2xs text-gray-4 sm:gap-10 sm:text-xs lg:gap-16 2xs:gap-5">
              {family?.dasValues?.map((dasValue, index) => (
                <p key={index} className="whitespace-nowrap">
                  {dasValue}
                </p>
              ))}
            </div>
          </div>

          {isBlackFridayDiscount && (
            <div className="absolute w-[181px] h-[107px] translate-x-44 -translate-y-48">
              <Image
                src="/images/christmas/product-sticker.webp"
                layout="fill"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
